import { useId, useState } from "react";
import { IconChevron } from "~/components/layout/nav-icons";
import { cn } from "~/lib/cn";
import { Button } from "./Button";

interface AccordionProps {
  question: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  /** `standard`: FAQ rows and method questions. `compact`: recovery blocks, the FAQ index on a phone. */
  variant?: "standard" | "compact";
  /** Anchor id — a deep link (`#acceso-reembolso`) opens the row (the consumer sets `open`). */
  id?: string;
  /** Controlled open state (a FAQ search or an anchor arrival owns it); omit for native toggling. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  className?: string;
}

/**
 * T872 S2 — the public disclosure, a native `<details>`/`<summary>` (the October
 * delivery's `.tfc-disclosure`): the question is the summary, + / − sits at the end,
 * a hairline closes the row. Native means keyboard, `open` and in-page anchors work
 * without script; `open`/`onToggle` let a FAQ search or an anchor arrival drive it.
 * Until this ticket `Accordion` was a Tailwind button + grid animation with no consumer.
 * Not `Disclosure` (below): that one hides an explanation behind a short text toggle.
 */
export function Accordion({
  question,
  children,
  defaultOpen = false,
  variant = "standard",
  id,
  open,
  onToggle,
  className,
}: AccordionProps) {
  const controlled = open !== undefined;
  return (
    <details
      id={id}
      className={cn("pub-disclosure", variant === "compact" && "is-compact", className)}
      open={controlled ? open : defaultOpen || undefined}
      onToggle={(e) => onToggle?.((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>{question}</summary>
      <div className="pub-disclosure-body">{children}</div>
    </details>
  );
}

interface DisclosureProps {
  /** The toggle's text while closed («Ver explicación») and while open («Ver menos»). */
  moreLabel: string;
  lessLabel: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * T831 — a short summary stays visible beside it; the explanation behind it opens on demand,
 * closed at first. A native button carries `aria-expanded` and `aria-controls`; the region is
 * `hidden` while closed, so it is out of the reading and tab order. Opening moves no focus and
 * scrolls nothing. Not `Accordion`: that one is a FAQ row whose header is the question.
 */
export function Disclosure({ moreLabel, lessLabel, children, className }: DisclosureProps) {
  const [open, setOpen] = useState(false);
  const regionId = useId();
  return (
    <div className={className}>
      <Button
        variant="text"
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((prev) => !prev)}
        className="mt-1 min-h-11 gap-1.5"
      >
        <span>{open ? lessLabel : moreLabel}</span>
        <IconChevron size={14} className={cn("transition-transform", open && "rotate-180")} />
      </Button>
      <div id={regionId} hidden={!open} className="mt-3 space-y-3">
        {children}
      </div>
    </div>
  );
}
